import { z } from 'zod';
import { UserSnapshotSchema } from './user';

export const PROCEDURE_LOG_EVENT_TYPE = {
  STARTED: 'PROCEDURE_STARTED',
  RESUMED: 'PROCEDURE_RESUMED',
  PAUSED: 'PROCEDURE_PAUSED',
  FINISHED: 'PROCEDURE_FINISHED',
  MODIFIED: 'PROCEDURE_MODIFIED',
  PLAN_EDITED: 'TREATMENT_PLAN_EDITED',
  SONICATION: 'DELIVERED_SONICATION',
  CRAVING_INDUCTION: 'CUEING_BLOCK_EVENT',
  SONICATION_MODIFIED: 'SONICATION_MODIFIED',
} as const;

export type ProcedureLogEventType = typeof PROCEDURE_LOG_EVENT_TYPE[keyof typeof PROCEDURE_LOG_EVENT_TYPE];

const BaseEventSchema = z.object({
  id: z.string().uuid(),
  type: z.nativeEnum(PROCEDURE_LOG_EVENT_TYPE),
  description: z.string(),
  timestamp: z.string().datetime(),
  createdBy: UserSnapshotSchema,
});

// Event specific detail schemas
const TreatmentPlanEditedDetailsSchema = z.object({
  target: z.object({
    ap: z.number(),
    rl: z.number(),
    si: z.number(),
    name: z.string(),
  }),
});

const CueingBlockEventDetailsSchema = z.object({
  status: z.string(),
  blockName: z.string(),
});

const DeliveredSonicationDetailsSchema = z.object({
  time: z.number().int(),
  power: z.number().int(),
  status: z.enum([ 'completed', 'stopped' ]),
  note: z.string().nullish(),
  isRampUp: z.boolean(),
  targets: z.array(z.object({
    ap: z.number().optional(),
    rl: z.number().optional(),
    si: z.number().optional(),
    name: z.string(),
  })),
});

const SonicationModifiedDetailsSchema = z.object({
  sonicationPassId: z.string(),
  order: z.number().int().optional(),
  status: z.string().optional(),
  power: z.number().int().optional(),
  time: z.number().int().optional(),
  note: z.string().nullish(),
  excludedFromSonication: z.boolean().optional(),
  coordinates: z.array(z.object({
    targetName: z.string(),
    editReasons: z.array(z.string()),
    newCoordinates: z.object({
      ap: z.number(),
      rl: z.number(),
      si: z.number(),
    }),
  })).optional(),
});

const ProcedureModifiedDetailsSchema = z.object({
  sonicationPower: z.number().int().optional(),
  note: z.string().optional(),
  coordinates: z.array(z.object({
    targetName: z.string(),
    note: z.string().nullish(),
    newCoordinates: z.object({
      ap: z.number(),
      rl: z.number(),
      si: z.number(),
    }),
  })).optional(),
});

// Define any schema for case when BE modifies
const AnyDetailsSchema = z.object({}).passthrough().optional().nullable();

// Discriminated union by `type`
export const ProcedureLogTimelineEventSchema = z.discriminatedUnion('type', [
  BaseEventSchema.extend({
    type: z.literal('PROCEDURE_STARTED'),
    details: AnyDetailsSchema,
  }),
  BaseEventSchema.extend({
    type: z.literal('TREATMENT_PLAN_EDITED'),
    details: TreatmentPlanEditedDetailsSchema,
  }),
  BaseEventSchema.extend({
    type: z.literal('CUEING_BLOCK_EVENT'),
    details: CueingBlockEventDetailsSchema,
  }),
  BaseEventSchema.extend({
    type: z.literal('DELIVERED_SONICATION'),
    details: DeliveredSonicationDetailsSchema,
  }),
  BaseEventSchema.extend({
    type: z.literal('SONICATION_MODIFIED'),
    details: SonicationModifiedDetailsSchema,
  }),
  BaseEventSchema.extend({
    type: z.literal('PROCEDURE_MODIFIED'),
    details: ProcedureModifiedDetailsSchema,
  }),
  BaseEventSchema.extend({
    type: z.literal('PROCEDURE_PAUSED'),
    details: AnyDetailsSchema,
  }),
  BaseEventSchema.extend({
    type: z.literal('PROCEDURE_RESUMED'),
    details: AnyDetailsSchema,
  }),
  BaseEventSchema.extend({
    type: z.literal('PROCEDURE_FINISHED'),
    details: AnyDetailsSchema,
  }),
]);

export const ProcedureLogTimelineResponseSchema = z.object({
  items: z.array(ProcedureLogTimelineEventSchema),
});

export type ProcedureLogTimelineEvent = z.infer<typeof ProcedureLogTimelineEventSchema>;
export type ProcedureLogTimelineResponse = z.infer<typeof ProcedureLogTimelineResponseSchema>;
